import { useCallback, useState } from 'react';
import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { environmentAtom } from '@data/fms/environment/states';
import { CUSTOM_DATA_SCOPES, projectAtom, useHasScope } from '@data/auth';
import type { AxiosResponse, AxiosError } from 'axios';
import { useNotification } from '@data/notification';
import { useTranslation } from 'react-i18next';
import { useAtomCallback } from 'jotai/utils';
import {
  type ScheduleTemplateAPISchema,
  type RemoteMonitoringSettingAPISchema,
} from './types';
import { CUSTOM_DATA_NAMESPACE, CUSTOM_DATA_KEY } from './constants';

const useRequest = <T>() => {
  const fmsAPI = useFMSAPI();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      keyName: string,
    ): Promise<Result<T, AxiosResponse>> => {
      const queryParams = new URLSearchParams();
      queryParams.append('namespace', CUSTOM_DATA_NAMESPACE);
      queryParams.append('key_name', keyName);
      try {
        const res = await fmsAPI.get(
          `/${projectId}/environments/${environmentId}/custom-data/?${queryParams.toString()}`,
        );
        return new Success(res.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  return {
    request,
  };
};

/**
 * 遠隔監視設定取得API
 */
export const useGetRemoteMonitoringSettingAPI = () => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { request } = useRequest<RemoteMonitoringSettingAPISchema>();
  const getHasScope = useHasScope();

  /**
   * 遠隔監視設定取得
   */
  const getRemoteMonitoringSetting = useAtomCallback(
    useCallback(
      async (get) => {
        const hasDescribeScope = getHasScope(
          CUSTOM_DATA_SCOPES.remote_monitoring_settings.Describe,
        );

        if (!hasDescribeScope) {
          return;
        }

        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.get_remote_monitoring_settings', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          CUSTOM_DATA_KEY.REMOTE_MONITORING_SETTINGS,
        );
        if (res.isFailure()) {
          if (res.value?.status === 404) {
            return {
              value: {
                notification: false,
              },
            };
          }
          notifyError({
            message: `${t('api.fms.get_remote_monitoring_settings', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return;
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, request, t, getErrorMessage, getHasScope],
    ),
  );

  return {
    state,
    getRemoteMonitoringSetting,
  };
};

/**
 * スケジュールテンプレート取得API
 */
export const useGetScheduleTemplatesAPI = () => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { request } = useRequest<ScheduleTemplateAPISchema>();

  /**
   * スケジュールテンプレート取得
   */
  const getScheduleTemplates = useAtomCallback(
    useCallback(
      async (get) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.get_schedule_templates', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          CUSTOM_DATA_KEY.SCHEDULE_TEMPLATE,
        );
        if (res.isFailure()) {
          if (res.value?.status === 404) {
            return {
              value: {
                items: [],
              },
            };
          }
          notifyError({
            message: `${t('api.fms.get_schedule_templates', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return;
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    getScheduleTemplates,
  };
};
